import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'

export const useFilterStore = defineStore('filter', () => {
  const filterHistory = ref({})

  const updateFilterHistory = (filterKey, managerId, filters) => {
    filterHistory.value[filterKey] = {
      [managerId]: filters
    }
  }

  return { filterHistory, updateFilterHistory }
})

export const useFilterValuesStore = defineStore('filterValues', () => {
  // Use shallow refs to avoid creating deep reactive proxies for large payloads.
  // Consumers only depend on the top-level key (cacheKey) and we always replace that entry.
  const values = shallowRef({})
  const categories = shallowRef({})
  const unfilteredCategories = shallowRef({})

  // Track pending updates to batch them together
  const pendingUpdates = ref({})
  let rafId = 0

  /**
   * Schedules a batched update for a specific key
   * This ensures we only apply updates once per animation frame
   */
  const scheduleUpdate = () => {
    if (rafId) return
    rafId = window.requestAnimationFrame(() => {
      rafId = 0
      const updatesByKey = pendingUpdates.value
      if (!updatesByKey) return

      // Build new shallow objects once to minimize reactive churn
      let nextValues = values.value
      let nextCategories = categories.value
      let nextUnfiltered = unfilteredCategories.value

      let changedValues = false
      let changedCategories = false
      let changedUnfiltered = false

      for (const key of Object.keys(updatesByKey)) {
        const updates = updatesByKey[key]
        if (updates.values !== undefined) {
          if (nextValues === values.value) nextValues = { ...nextValues }
          nextValues[key] = updates.values
          changedValues = true
        }
        if (updates.categories !== undefined) {
          if (nextCategories === categories.value) nextCategories = { ...nextCategories }
          nextCategories[key] = updates.categories
          changedCategories = true
        }
        if (updates.unfilteredCategories !== undefined) {
          if (nextUnfiltered === unfilteredCategories.value) nextUnfiltered = { ...nextUnfiltered }
          nextUnfiltered[key] = updates.unfilteredCategories
          changedUnfiltered = true
        }
        delete updatesByKey[key]
      }

      // Replace shallow refs so dependents recompute once per group
      if (changedValues) values.value = nextValues
      if (changedCategories) categories.value = nextCategories
      if (changedUnfiltered) unfilteredCategories.value = nextUnfiltered
    })
  }

  /**
   * Batch updates all filter data at once for a specific key
   * This is more efficient than calling individual setters
   */
  const batchUpdate = (key, newValues, newCategories, newUnfilteredCategories) => {
    if (!pendingUpdates.value[key]) {
      pendingUpdates.value[key] = {}
    }

    if (newValues !== undefined) {
      pendingUpdates.value[key].values = newValues
    }
    if (newCategories !== undefined) {
      pendingUpdates.value[key].categories = newCategories
    }
    if (newUnfilteredCategories !== undefined) {
      pendingUpdates.value[key].unfilteredCategories = newUnfilteredCategories
    }

    scheduleUpdate()
  }

  return {
    categories,
    values,
    unfilteredCategories,
    batchUpdate
  }
})
